import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Textarea,
  Text,
  VStack,
  HStack,
  useToast,
} from "@chakra-ui/react";
import services from "../services";
import { extractErrorMessage } from "../utils/orderItemsActionUtils";

interface DeleteOrderConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderNo: string;
  orderType: string;
  orderStatus: string;
  origin?: string;
  onDeleteSuccess: () => void;
}

const bodyCopyProps = {
  fontSize: "lg" as const,
  lineHeight: "tall" as const,
  fontWeight: "normal" as const,
  color: "gray.800",
};

const getOriginBlockLabel = (origin?: string): string => {
  if (origin === "supplierCancelledItems")
    return "Supplier Cancelled Quantities";
  if (origin === "cboItems") return "CBO Quantities";
  return "Customer Ordered Quantities";
};

const DeleteOrderConfirmationDialog: React.FC<
  DeleteOrderConfirmationDialogProps
> = ({
  isOpen,
  onClose,
  orderNo,
  orderType,
  orderStatus,
  origin,
  onDeleteSuccess,
}) => {
  const cancelRef = React.useRef(null);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      await services.deleteOrder(orderNo, reason.trim());
      toast({
        title: `Order ${orderNo} has been voided successfully`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      setReason("");
      onClose();
      onDeleteSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to void order",
        description: extractErrorMessage(error),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason("");
      onClose();
    }
  };

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={handleClose}
      returnFocusOnClose={false}
      isCentered
      closeOnOverlayClick={!isLoading}
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          maxW="2xl"
          borderRadius="lg"
          boxShadow="xl"
          aria-label="Void order confirmation"
        >
          <AlertDialogBody pt={6} pb={5} px={{ base: 5, md: 8 }}>
            <VStack align="stretch" spacing={4}>
              <Text {...bodyCopyProps} fontWeight="semibold" color="gray.900">
                Are you sure you want to void this order?
              </Text>
              <Text {...bodyCopyProps}>
                Note: Part quantities of this order will be restored to the
                enquiry's <strong>{getOriginBlockLabel(origin)}</strong>.
              </Text>
              <VStack align="stretch" spacing={3}>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Order Number
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {orderNo}
                  </Text>
                </Text>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Order Type
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {orderType}
                  </Text>
                </Text>
                <Text fontSize="lg" lineHeight="tall" color="gray.800">
                  <Text
                    as="span"
                    fontWeight="semibold"
                    color="gray.900"
                    fontSize="lg"
                  >
                    Order Status
                  </Text>
                  {": "}
                  <Text as="span" fontWeight="normal" fontSize="lg">
                    {orderStatus}
                  </Text>
                </Text>
              </VStack>
              <Textarea
                placeholder="Enter reason for voiding this order"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={256}
                isDisabled={isLoading}
                fontSize="lg"
                size="lg"
                rows={3}
              />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter
            flexDirection="column"
            alignItems="stretch"
            gap={0}
            pt={2}
            pb={6}
            px={{ base: 5, md: 8 }}
          >
            <HStack justify="flex-end" spacing={3} w="100%">
              <Button
                ref={cancelRef}
                onClick={handleClose}
                isDisabled={isLoading}
                size="md"
                fontSize="lg"
                py={2}
                px={4}
                minH="auto"
                h="auto"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                bg="red.500"
                onClick={handleSubmit}
                isDisabled={!reason.trim() || isLoading}
                isLoading={isLoading}
                loadingText="Voiding…"
                size="md"
                fontSize="lg"
                py={2}
                px={4}
                minH="auto"
                h="auto"
              >
                Void Order
              </Button>
            </HStack>
            {isLoading && (
              <Text
                textAlign="center"
                fontSize="sm"
                lineHeight="short"
                color="gray.600"
                pt={4}
                px={2}
              >
                Please wait while we perform the action. Don&apos;t close the
                window.
              </Text>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteOrderConfirmationDialog;
